import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import type { PreviewLink } from '../../types/previewLink';
import {
  generatePreviewLink,
  getPreviewLink,
  previewPublicUrl,
  revokePreviewLink,
} from './api';

// Ad Preview Mockups Stage 1 §3.1, §3.4. Single-row modal used by both
// the Build View sticky-nav action AND the Master Grid row kebab.
//
// Lifecycle inside the modal:
//   1. Open with no link → button reads "Generate Preview" (or
//      "Re-generate Preview" when initialLink is non-null).
//   2. User clicks → POSTs to /preview-links, modal flips to URL display
//      with copy + revoke affordances.
//   3. Revoke → returns the updated link (revokedAt set), copy button
//      stays available but a "Revoked" badge appears.
//
// The Build View / RowActionsMenu wrappers pass `initialLink` when the
// row already has a `latestPreviewLinkId`. The modal then shows the
// existing URL up-front; the AM can copy / revoke / regenerate.

type Props = {
  rolloutId: string;
  rowId: string;
  locationName: string;
  // Callers pass EITHER the full link (when they already have it) OR the
  // id of the row's latestPreviewLinkId (which the modal fetches on open).
  // Both null = brand-new generation flow with no existing state.
  initialLink?: PreviewLink | null;
  existingLinkId?: string | null;
  onClose: () => void;
};

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function GeneratePreviewModal({
  rolloutId,
  rowId,
  locationName,
  initialLink,
  existingLinkId,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [link, setLink] = useState<PreviewLink | null>(initialLink ?? null);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Lazy-fetch when the caller only knows the id (typical entry: row has
  // latestPreviewLinkId set, BuildView / RowActionsMenu open the modal
  // without pre-fetching). Skipped when initialLink is provided OR when
  // we've already minted a fresh link via the generate mutation.
  const shouldFetch = !link && !!existingLinkId;
  const fetchQuery = useQuery({
    queryKey: ['previewLink', existingLinkId],
    queryFn: () => getPreviewLink(existingLinkId!),
    enabled: shouldFetch,
  });
  useEffect(() => {
    if (fetchQuery.data && !link) setLink(fetchQuery.data);
  }, [fetchQuery.data, link]);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const generateMut = useMutation({
    mutationFn: () => generatePreviewLink(rolloutId, rowId),
    onSuccess: (next) => {
      setLink(next);
      setError(null);
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'rows'] });
    },
    onError: (err: Error) => {
      setError(
        err instanceof ApiError ? err.message : 'Failed to generate preview link.',
      );
    },
  });

  const revokeMut = useMutation({
    mutationFn: () => {
      if (!link) throw new Error('No link to revoke.');
      return revokePreviewLink(link.id);
    },
    onSuccess: (next) => {
      setLink(next);
      setError(null);
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'rows'] });
    },
    onError: (err: Error) => {
      setError(
        err instanceof ApiError ? err.message : 'Failed to revoke preview link.',
      );
    },
  });

  const url = link ? previewPublicUrl(link.token) : null;
  const isRevoked = link?.revokedAt !== null && link?.revokedAt !== undefined;
  const expired = link
    ? new Date(link.expiresAt).getTime() <= Date.now()
    : false;

  const onCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      setError('Could not copy — copy manually instead.');
    }
  };

  const isPending = generateMut.isPending || revokeMut.isPending;
  const buttonLabel = link ? 'Re-generate Preview' : 'Generate Preview';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        className="w-full max-w-xl rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="generate-preview-title"
      >
        <div className="p-6 space-y-4">
          <div className="space-y-1">
            <h2
              id="generate-preview-title"
              className="text-lg font-semibold text-slate-900"
            >
              Share preview — {locationName}
            </h2>
            <p className="text-sm text-slate-500">
              Public, no-auth URL. Default expiry is 14 days. AMs can revoke
              early; re-generating creates a NEW URL but the previous one
              keeps working until its own expiry.
            </p>
          </div>

          {shouldFetch && fetchQuery.isLoading && (
            <div className="py-4">
              <CenteredSpinner />
            </div>
          )}

          {link && url ? (
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">
                  Preview URL
                </label>
                <div className="flex items-stretch gap-2">
                  <input
                    type="text"
                    readOnly
                    value={url}
                    className="flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm font-mono"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <Button type="button" variant="outline" onClick={onCopy}>
                    {copyState === 'copied' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <dt className="text-slate-500">Generated</dt>
                <dd className="text-slate-900">{formatExpiry(link.generatedAt)}</dd>
                <dt className="text-slate-500">Expires</dt>
                <dd className="text-slate-900">{formatExpiry(link.expiresAt)}</dd>
                <dt className="text-slate-500">Views</dt>
                <dd className="text-slate-900">{link.viewCount}</dd>
              </dl>

              {isRevoked && (
                <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  This link has been revoked. The URL above no longer works.
                  Re-generate to create a fresh URL.
                </p>
              )}
              {!isRevoked && expired && (
                <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  This link has expired. Re-generate to create a fresh URL.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              No preview link yet — click <strong>Generate Preview</strong> to
              create one.
            </p>
          )}

          {error && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Close
            </button>
            {link && !isRevoked && (
              <Button
                type="button"
                variant="outline"
                onClick={() => revokeMut.mutate()}
                disabled={isPending}
              >
                {revokeMut.isPending ? 'Revoking…' : 'Revoke'}
              </Button>
            )}
            <Button
              type="button"
              onClick={() => generateMut.mutate()}
              disabled={isPending}
            >
              {generateMut.isPending ? 'Generating…' : buttonLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
